/** HAR 1.2 export of the captured debug-toolbar requests.
 *
 * Built entirely from what the axios interceptors recorded (src/api/debugMode.ts),
 * so it is honest about what the browser exposes to JS: no httpVersion, no
 * DNS/TCP/TLS breakdown (`timings` = all-wait), request bodies omitted, and
 * response bodies capped at capture time. Credentialed request headers were
 * already redacted at capture. The backend SQL/timing summary rides along as
 * the spec-sanctioned custom `_debug` field per entry — standard viewers
 * ignore `_`-prefixed fields.
 */
import type { DebugRequestEntry } from '@/api/debugMode';

interface HarNameValue {
  name: string;
  value: string;
}

function nameValues(record: Record<string, string>): HarNameValue[] {
  return Object.entries(record).map(([name, value]) => ({ name, value }));
}

function startedDateTime(entry: DebugRequestEntry): string {
  // ts is stamped at response capture; the request started durationMs earlier.
  return new Date(entry.ts - entry.durationMs).toISOString();
}

export function buildHar(entries: readonly DebugRequestEntry[]) {
  const ordered = [...entries].sort(
    (a, b) => a.ts - a.durationMs - (b.ts - b.durationMs),
  );
  const first = ordered[0];
  return {
    log: {
      version: '1.2',
      creator: { name: 'Curated Planet Ops Console — API debug toolbar', version: '1' },
      pages: [
        {
          startedDateTime: first ? startedDateTime(first) : new Date().toISOString(),
          id: 'page_1',
          title: 'Ops Console API debug capture',
          pageTimings: { onContentLoad: -1, onLoad: -1 },
        },
      ],
      entries: ordered.map((e) => ({
        pageref: 'page_1',
        startedDateTime: startedDateTime(e),
        time: e.durationMs,
        request: {
          method: e.method,
          url: e.url,
          httpVersion: '',
          cookies: [],
          headers: nameValues(e.requestHeaders),
          queryString: [...new URLSearchParams(e.query)].map(([name, value]) => ({
            name,
            value,
          })),
          headersSize: -1,
          bodySize: -1,
        },
        response: {
          status: e.status ?? 0,
          statusText: e.statusText ?? '',
          httpVersion: '',
          cookies: [],
          headers: nameValues(e.responseHeaders),
          content: {
            size: e.responseBodySize,
            mimeType: e.responseMimeType ?? 'x-unknown',
            ...(e.responseBody !== null ? { text: e.responseBody } : {}),
            ...(e.responseBodyTruncated
              ? { comment: 'Body truncated by the debug-toolbar capture cap.' }
              : {}),
          },
          redirectURL: '',
          headersSize: -1,
          bodySize: e.responseBodySize,
        },
        cache: {},
        timings: { send: 0, wait: e.durationMs, receive: 0 },
        ...(e.requestId !== null ? { _requestId: e.requestId } : {}),
        ...(e.summary !== null ? { _debug: e.summary } : {}),
      })),
    },
  };
}

export function downloadHar(entries: readonly DebugRequestEntry[]): void {
  const blob = new Blob([JSON.stringify(buildHar(entries), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  a.download = `console-debug-${stamp}.har`;
  a.click();
  URL.revokeObjectURL(url);
}
